import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import DataTable from './DataTable';
import FormModal from './FormModal';
import { getActivities, createActivity, updateActivity, deleteActivity, getDestinations } from '../../../services/travelInventoryService';

const ActivitiesManager = ({ searchQuery }) => {
  const [activities, setActivities] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [activitiesData, destinationsData] = await Promise.all([
        getActivities(),
        getDestinations(),
      ]);
      setActivities(activitiesData || []);
      setDestinations(destinationsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;
    
    try {
      await deleteActivity(id);
      await loadData();
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Failed to delete activity');
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingItem) {
        await updateActivity(editingItem?.id, formData);
      } else {
        await createActivity(formData);
      }
      setIsModalOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error saving activity:', error);
      throw error;
    }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'activity_type', label: 'Type', sortable: true },
    { key: 'location', label: 'Location', sortable: true },
    { key: 'price', label: 'Price', type: 'currency' },
    { key: 'average_rating', label: 'Rating', sortable: true },
  ];

  const formFields = [
    {
      name: 'destination_id',
      label: 'Destination',
      type: 'select',
      required: true,
      options: destinations?.map((d) => ({ value: d?.id, label: `${d?.name}, ${d?.country}` })),
    },
    { name: 'name', label: 'Activity Name', type: 'text', required: true },
    {
      name: 'activity_type',
      label: 'Type',
      type: 'select',
      required: true,
      options: [
        { value: 'sightseeing', label: 'Sightseeing' },
        { value: 'adventure', label: 'Adventure' },
        { value: 'cultural', label: 'Cultural' },
        { value: 'entertainment', label: 'Entertainment' },
        { value: 'sports', label: 'Sports' },
        { value: 'wellness', label: 'Wellness' },
        { value: 'shopping', label: 'Shopping' },
        { value: 'nightlife', label: 'Nightlife' },
      ],
    },
    { name: 'description', label: 'Description', type: 'textarea', required: true },
    { name: 'short_description', label: 'Short Description', type: 'textarea' },
    { name: 'location', label: 'Location', type: 'text' },
    { name: 'address', label: 'Address', type: 'text' },
    { name: 'latitude', label: 'Latitude', type: 'number', step: '0.0000001' },
    { name: 'longitude', label: 'Longitude', type: 'number', step: '0.0000001' },
    { name: 'duration_minutes', label: 'Duration (minutes)', type: 'number' },
    { name: 'price', label: 'Price', type: 'number', step: '0.01', required: true },
    { name: 'currency', label: 'Currency', type: 'text', defaultValue: 'PHP' },
    {
      name: 'price_range',
      label: 'Price Range',
      type: 'select',
      options: [
        { value: 'budget', label: 'Budget' },
        { value: 'moderate', label: 'Moderate' },
        { value: 'expensive', label: 'Expensive' },
        { value: 'luxury', label: 'Luxury' },
      ],
    },
    { name: 'image_url', label: 'Activity Image', type: 'image-upload' },
    { name: 'image_alt', label: 'Image Alt Text', type: 'text' },
    { name: 'website', label: 'Website', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'booking_url', label: 'Booking URL', type: 'text' },
    { name: 'min_participants', label: 'Min Participants', type: 'number', defaultValue: 1 },
    { name: 'max_participants', label: 'Max Participants', type: 'number' },
    { name: 'age_restriction', label: 'Age Restriction', type: 'text' },
    {
      name: 'difficulty_level',
      label: 'Difficulty Level',
      type: 'select',
      options: [
        { value: 'easy', label: 'Easy' },
        { value: 'moderate', label: 'Moderate' },
        { value: 'challenging', label: 'Challenging' },
        { value: 'difficult', label: 'Difficult' },
      ],
    },
    {
      name: 'features',
      label: 'Features',
      type: 'checkbox-group',
      options: [
        { value: 'guided_tour', label: 'Guided Tour' },
        { value: 'equipment_included', label: 'Equipment Included' },
        { value: 'meals_included', label: 'Meals Included' },
        { value: 'transport_included', label: 'Transport Included' },
        { value: 'photo_service', label: 'Photo Service' },
        { value: 'insurance_included', label: 'Insurance Included' },
        { value: 'hotel_pickup', label: 'Hotel Pickup' },
        { value: 'family_friendly', label: 'Family Friendly' },
      ],
    },
    { name: 'is_featured', label: 'Featured', type: 'checkbox' },
  ];

  const filteredData = activities?.filter((item) => {
    const matchesSearch = searchQuery
      ? item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      : true;
    const matchesFilter = filterType === 'all' || item?.activity_type === filterType;
    return matchesSearch && matchesFilter;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Activities</h2>
          <p className="text-gray-600 mt-1">Manage tours, experiences, and activities</p>
        </div>
        <Button iconName="Plus" onClick={handleCreate}>
          Add Activity
        </Button>
      </div>

      <div className="mb-4">
        <Select
          value={filterType}
          onChange={setFilterType}
          options={[
            { value: 'all', label: 'All Types' },
            { value: 'sightseeing', label: 'Sightseeing' },
            { value: 'adventure', label: 'Adventure' },
            { value: 'cultural', label: 'Cultural' },
            { value: 'sports', label: 'Sports' },
          ]}
          className="max-w-xs"
        />
      </div>

      <DataTable
        data={filteredData}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        title={editingItem ? 'Edit Activity' : 'Add Activity'}
        fields={formFields}
        initialData={editingItem}
      />
    </div>
  );
};

export default ActivitiesManager;